import _ from 'lodash'
import client, {fetch, post, put, remove} from '@/base-http'
import Carrier from '@/modules/shipping/model/Carrier'
import Rate from '@/modules/shipping/model/Rate'
import Region from '@/modules/shipping/model/Region'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import { PostalCode } from '@/modules/geo/types';
import CalculationMode from '../model/CalculationMode';

/**
 * Obtiene la lista de empresas de transporte registradas en la aplicación
 */
export const getCarriers = async (): Promise<Carrier[]> => {
    const response = await client.get('shop/shipping/carriers')

    return response.data.map((carrier: Carrier) => Carrier.convert(carrier))
}

/**
 * Obtiene de la api la lista de tarifas de envío
 */
export const getRates = async () => {
    const data: KeyValuePair[] = (await client.get('shop/shipping/rates')).data
    if (!Array.isArray(data)) {
        throw new Error(`Se esperaba un array con las tarifas pero se obtuvo el tipo ${typeof data}`)
    }

    return data.map(r => Rate.deserialize(r))
}

/**
 * Obtiene una tarifa registrada en Artisan
 * @param id
 */
export const getRate = async (id: number) => {
    const response = await client.get(`shop/shipping/rates/${id}`)

    const rate = Rate.deserialize(response.data)
    return {
        rate,
        regions: response.data.regions.map((r: KeyValuePair) => {
            const region = Region.convert(r)
            region.rate = rate

            return region
         }),
    }
}

/**
 * Elimina una tarifa
 *
 * @param id de la tarifa por parámetro.
 */
export const deleteRate = async (value: number | Rate) => {
    if (value instanceof Rate) {
        const id = value.id
    }
    const response = await client.delete(`shop/shipping/rates/${value}`)

    return response.data.success || false
}

/**
 * Registra una nueva tarifa en el servidor
 *
 * @param rate
 * @param regions
 */
export const createRate = async (rate: Rate, regions: Region[]): Promise<{rate: Rate, regions: Region[]}> => {
    const data = Rate.serialize(rate)
    data.regions = regions.map(r => r.serialize())
    const createdData = (await client.post('shop/shipping/rate', data)).data
    const regions2 = createdData.regions.map((r: KeyValuePair) => Region.convert(r))

    // Si los datos creados tienen regiones y el array de las mismas,
    const deserializedRate = Rate.deserialize(createdData)
    if (regions2.length > 0) {
        regions2.forEach((region: Region) => {
            region.rate.id = deserializedRate.id
        })
    }

    return {
        rate: deserializedRate,
        regions: regions2,
    }
}

/**
 * Actualiza una tarifa en el servidor
 * @param rate
 * @param regions
 */
export const updateRate = async (rate: Rate, regions: Region[]): Promise<{rate: Rate, regions: Region[]}> => {
    const data = Rate.serialize(rate)
    data.regions = regions.map(r => r.serialize())

    const updatedData = (await client.put(`/shop/shipping/rates/${rate.id}`, data)).data
    const updatedRate = Rate.deserialize(updatedData)

    return {
        rate: updatedRate,
        regions: updatedData.regions.map((r: KeyValuePair) => {
             const region = Region.convert(r)
             region.rate = updatedRate

             return region
        }),
    }
}

export interface CalculationInput {
    quantity: number
    postalCode: PostalCode
    rate: Rate
    mode: CalculationMode
}

export interface CalculationResult {
    isShippingFree: boolean
    matched: boolean
    weight?: {
        id: number,
        value: number,
        region: string,
    }
}

export async function calculateCost (input: CalculationInput) {
    const data = await fetch('/shop/shipping/calculator/cost', {
        params: {
            weight: input.quantity,
            postalCode: input.postalCode.id,
            mode: input.mode,
            rate: input.rate.id,
        },
    })

    const result: CalculationResult = {
        isShippingFree: data.is_shipping_free,
        matched: data.matched,
    }

    if ('weight' in data) {
        result.weight = {
            id: data.weight.id,
            value: data.weight.value,
            region: data.weight.region,
        }
    }

    return result
}


/**
 * Crea un nuevo transportista en la base de datos
 *
 * @param carrier
 */
export async function createCarrier (carrier: Carrier) {
    if (!carrier.isNew) {
        throw new Error('No se puede volver crear un transportista que ya existe')
    }

    const result =  await post('/shop/shipping/carrier', carrier.serialize())

    return Carrier.convert(result)
}


/**
 * Crea un nuevo transportista en la base de datos
 *
 * @param carrier
 */
export async function updateCarrier (carrier: Carrier) {
    if (carrier.isNew) {
        throw new Error('No se puede actualizar un transportista que no ha sido registrado')
    }

    const result = await put(
        `/shop/shipping/carrier/${carrier.id}`,
        carrier.serialize(),
    )

    return Carrier.convert(result)
}


export async function deleteCarrier (id: number) {
    try {
        const result = await remove(`/shop/shipping/carrier/${id}`)
        return result.success || false
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

